import type { OpeningHour as BusinessHour } from '@cheqroom/api';
import { DateTime, DayOfWeek, TimeOfDay } from '@cheqroom/date-time';

export const DEFAULT_OPENING_HOURS_START = TimeOfDay.fromString('9:00:00');
export const DEFAULT_OPENING_HOURS_END = TimeOfDay.fromString('17:00:00');
export const DEFAULT_END_OF_DAY = TimeOfDay.fromString('23:45:00');

/**
 * An opening-hours row keyed by ISO weekday (1 = Monday), the shape these utils work in: the python-api
 * row (`OpeningHour` in `@cheqroom/api`, on groups and locations) with its 0-based `dayOfWeek` swapped
 * for `isoWeekday`, so the weekday has one source.
 */
export type OpeningHour = Omit<BusinessHour, 'dayOfWeek'> & { isoWeekday: number };

/**
 * Builds the effective opening hours for a location: its own hours when permitted
 * and present, otherwise the group-wide hours. Both come as python-api rows (0-based
 * `dayOfWeek`); the result is keyed by ISO weekday and sorted.
 */
export function resolveOpeningHours(
	locationHours: BusinessHour[] | null | undefined,
	groupHours: BusinessHour[] | null | undefined,
	allowLocationBusinessHours: boolean
): OpeningHour[] {
	const source = (allowLocationBusinessHours && locationHours ? locationHours : groupHours) ?? [];

	return source
		.map(({ dayOfWeek, openTime, closeTime }) => ({ isoWeekday: dayOfWeek + 1, openTime, closeTime }))
		.sort((a, b) => a.isoWeekday - b.isoWeekday || a.openTime - b.openTime);
}

/**
 * Hours for the calendar day's weekday. Opening-hour rows are keyed by ISO weekday;
 * a pure Y-M-D has the same weekday in every zone, so browser/user weekday is correct.
 */
export function getHoursForDay(openingHours: OpeningHour[], day: DateTime): OpeningHour[] {
	if (!openingHours.length) return [];

	const dayOfWeek = day.dayOfWeek;
	return openingHours.filter((hour) => dayOfWeek.isSameDay(DayOfWeek.fromISOWeekday(hour.isoWeekday)));
}

/**
 * Start of the location's business day as a **user** wall-clock DateTime.
 *
 * Opening minutes are workspace wall-clock; python-api persists booking datetimes as
 * user wall-clock floating values — convert before returning. Always snaps to first open
 * (day-based / month `force` snaps). Mid-window times for hour-precision workspaces are
 * preserved by `resolveSnapBookingDates` (`useHours && !force`), not here.
 *
 * - No opening-hours config → calendar midnight (user frame).
 * - Configured workspace, closed weekday → DEFAULT 09:00 workspace → user (same calendar day).
 * - Configured weekday → first open workspace → user (same calendar day).
 * - When day-clamp would put open **after** close on the selected day (large positive
 *   workspace offset), fall back to calendar start so bounds stay ordered — see
 *   {@link resolveOrderedBusinessDayBounds}.
 */
export function toBusinessDayStart(
	day: DateTime,
	hoursForDay: OpeningHour[],
	hasOpeningHoursConfig: boolean
): DateTime {
	if (!hasOpeningHoursConfig) return day.startOfDay();

	const openMinutes = hoursForDay.length ? hoursForDay[0].openTime : DEFAULT_OPENING_HOURS_START.minutesSinceMidnight;

	return DateTime.fromWorkspaceWallClock(day, openMinutes, { boundary: 'open' });
}

/**
 * End of the location's business day as a **user** wall-clock DateTime.
 * A configured workspace with no hours for this weekday returns calendar end-of-day
 * (user-frame bound — not converted workspace minutes).
 */
export function toBusinessDayEnd(day: DateTime, hoursForDay: OpeningHour[], hasOpeningHoursConfig: boolean): DateTime {
	if (!hasOpeningHoursConfig || !hoursForDay.length) return day.endOfDay();

	const closeMinutes = hoursForDay[hoursForDay.length - 1].closeTime;
	return DateTime.fromWorkspaceWallClock(day, closeMinutes, { boundary: 'close' });
}

/**
 * Open/close snaps for a calendar day, guaranteed `start < end` on that Y-M-D.
 *
 * Under large workspace≻user offsets, day-clamping independently can yield open HH:mm
 * after close (e.g. Auckland 09:00–17:00 → UTC 21:00 / 05:00). The selected user day then
 * only contains the morning portion through close — use calendar midnight as start.
 */
export function resolveOrderedBusinessDayBounds(
	day: DateTime,
	hoursForDay: OpeningHour[],
	hasOpeningHoursConfig: boolean
): { start: DateTime; end: DateTime } {
	let start = toBusinessDayStart(day, hoursForDay, hasOpeningHoursConfig);
	const end = toBusinessDayEnd(day, hoursForDay, hasOpeningHoursConfig);

	if (!start.isBefore(end)) {
		start = day.startOfDay();
	}

	return { start, end };
}

/**
 * Business-day snappers for a fixed set of opening hours.
 *
 * Pure counterpart to the `useOpeningHours` getters, so an async caller can snap against hours
 * it has just fetched instead of the ones captured in its render closure.
 */
export function createBusinessHoursSnappers(openingHours: OpeningHour[]): {
	startOfDay: (day: DateTime) => DateTime;
	endOfDay: (day: DateTime) => DateTime;
} {
	const hasOpeningHoursConfig = openingHours.length > 0;
	const boundsFor = (day: DateTime) =>
		resolveOrderedBusinessDayBounds(day, getHoursForDay(openingHours, day), hasOpeningHoursConfig);

	return {
		startOfDay: (day) => boundsFor(day).start,
		endOfDay: (day) => boundsFor(day).end,
	};
}
